import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NFTCard, NFTCardSkeleton } from "./NFTCard";
import { MarketplaceNFT } from "@/hooks/useMarketplaceNFTs";
import { cn } from "@/lib/utils";

interface NFTCarouselProps {
  nfts: MarketplaceNFT[];
  loading?: boolean;
  showTrending?: boolean;
  className?: string;
  onBuySuccess?: () => void | Promise<void>;
}

export function NFTCarousel({ nfts, loading = false, showTrending = false, className, onBuySuccess }: NFTCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    dragFree: true,
  });
  
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  if (loading) {
    return (
      <div className={cn("overflow-hidden", className)} ref={emblaRef}>
        <div className="flex gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex-none w-[85%] sm:w-[45%] md:w-[30%] lg:w-[24%]">
              <NFTCardSkeleton />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (nfts.length === 0) {
    return null;
  }

  const scrollSnapCount = emblaApi?.scrollSnapList().length || 0;

  return (
    <div className={cn("relative group", className)}>
      {/* Carousel container */}
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-3">
          {nfts.map((nft) => (
            <div 
              key={nft.id} 
              className="flex-none w-[85%] sm:w-[45%] md:w-[30%] lg:w-[24%]"
            >
              <NFTCard nft={nft} showTrending={showTrending} onBuySuccess={onBuySuccess} />
            </div>
          ))}
        </div>
      </div>

      {/* Navigation arrows - hidden on mobile, visible on larger screens */}
      <Button
        variant="outline"
        size="icon"
        className={cn(
          "absolute left-2 top-1/2 -translate-y-1/2 z-10 hidden md:flex",
          "bg-background/80 backdrop-blur-sm border-border/50 shadow-lg",
          "opacity-0 group-hover:opacity-100 transition-opacity",
          !canScrollPrev && "hidden"
        )}
        onClick={scrollPrev}
        disabled={!canScrollPrev}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      <Button
        variant="outline"
        size="icon"
        className={cn(
          "absolute right-2 top-1/2 -translate-y-1/2 z-10 hidden md:flex",
          "bg-background/80 backdrop-blur-sm border-border/50 shadow-lg",
          "opacity-0 group-hover:opacity-100 transition-opacity",
          !canScrollNext && "hidden"
        )}
        onClick={scrollNext}
        disabled={!canScrollNext}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>

      {/* Dot indicators - visible on mobile */}
      {scrollSnapCount > 1 && (
        <div className="flex justify-center gap-1.5 mt-4 md:hidden">
          {Array.from({ length: Math.min(scrollSnapCount, 8) }).map((_, i) => (
            <button
              key={i}
              className={cn(
                "w-2 h-2 rounded-full transition-all duration-200",
                selectedIndex === i 
                  ? "bg-primary w-4" 
                  : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
              )}
              onClick={() => emblaApi?.scrollTo(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
          {scrollSnapCount > 8 && (
            <span className="text-xs text-muted-foreground ml-1">
              +{scrollSnapCount - 8}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
